import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from './api';
import { LINK_PAUSED_COPY, isLinkPaused } from './errors';
import { MIN_PASSWORD, PASSWORD_HINT } from './passwordPolicy';
import { useAuth } from './AuthContext';
import { Input } from '../components/ui/input';
import PasswordInput from './shared/PasswordInput';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { BRAND } from './kit/tokens';
import { Loader2, Home, ArrowRight } from 'lucide-react';

// Two invitation flows share this page, on separate query params:
//   ?invite=<code>  — lead invite (borrower / realtor), POST /invites/accept
//   ?token=<token>  — staff invitation (ticket 58), /invitations/t/{token}
export default function InviteAcceptPage() {
  const [params] = useSearchParams();
  const inviteCode = params.get('invite');
  const staffToken = params.get('token');
  if (staffToken) return <StaffInvite token={staffToken} />;
  if (inviteCode) return <LeadInvite inviteCode={inviteCode} />;
  return null;
}

function Shell({ title, subtitle, children, tagline }) {
  return (
    <div className="min-h-screen flex" style={{ background: '#faf7f2' }}>
      <div className="hidden lg:flex lg:w-5/12 relative overflow-hidden" style={{ background: `linear-gradient(160deg, ${BRAND.navyDeep} 0%, ${BRAND.navy} 60%, ${BRAND.navyLight} 100%)` }}>
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(141,198,63,0.35) 0%, transparent 45%)' }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"><Home size={14} /> BrokerForYou</Link>
          <div>
            <img src="/assets/tcl-logo-white.png" alt="Top Choice Lending" className="h-14" />
            <h1 className="font-serif text-5xl font-semibold mt-8">You're <span className="text-brand-accent-soft">invited</span>.</h1>
            <p className="mt-4 text-white/70 text-lg max-w-md">{tagline}</p>
          </div>
          <div className="text-xs text-white/40">Secure invite link</div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md" data-testid="invite-accept-page">
          <h2 className="font-serif text-3xl font-semibold text-brand-navy">{title}</h2>
          {subtitle && <p className="text-slate-500 mt-2">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

function LeadInvite({ inviteCode }) {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/invites/accept', { invite_code: inviteCode, password, name });
      toast({ title: 'Account created!', description: 'Please sign in.' });
      navigate('/portal/login', { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail || 'Invalid invite';
      toast({ title: 'Failed', description: detail });
      setFailed(typeof detail === 'string' ? detail : 'Invalid invite');
    }
    setBusy(false);
  };

  return (
    <Shell title="Complete your signup" subtitle="Set your password to access the portal."
      tagline="Set a password to access your secure portal and track your loan in real time.">
      {failed && (
        <p className="mb-4 text-sm text-red-600" data-testid="invite-failed">
          {failed}. <Link to="/portal/login" className="underline" data-testid="invite-sign-in-instead">Sign in instead</Link>
        </p>
      )}
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" /></div>
        <div><Label>Password</Label><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required minLength={MIN_PASSWORD} containerClassName="mt-1.5" placeholder={PASSWORD_HINT} /></div>
        <Button type="submit" disabled={busy} variant="accent" className="w-full justify-center">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Creating...</> : <>Activate Account <ArrowRight size={14} /></>}
        </Button>
      </form>
    </Shell>
  );
}

const ROLE_LABELS = { admin: 'Admin', loan_officer: 'Loan Officer', loan_officer_assistant: 'LO Assistant', processor: 'Processor' };
const detailOf = (e) => e.response?.data?.detail;
const codeOf = (e) => (typeof detailOf(e) === 'object' ? detailOf(e)?.code : null);
const messageOf = (e) => (typeof detailOf(e) === 'object' ? detailOf(e)?.message : detailOf(e)) || 'Something went wrong';

function StaffInvite({ token }) {
  const { user, loading: authLoading, completeGoogleLogin, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const here = `/portal/invite?token=${encodeURIComponent(token)}`;

  useEffect(() => {
    api.get(`/invitations/t/${encodeURIComponent(token)}`)
      .then(({ data }) => { setInfo(data); if (data.invitee_name) setName((n) => n || data.invitee_name); })
      .catch((e) => setError(e.response?.status === 410 ? 'This invitation has expired or was revoked.'
        : isLinkPaused(e) ? LINK_PAUSED_COPY
        : e.response?.status === 404 ? 'This invitation link is not valid.' : messageOf(e)));
  }, [token]);

  // Where the client goes after a successful accept.
  const finish = (data) => {
    if (data.mfa_required) {
      // An admin seat needs a second factor before the next request; LoginPage
      // hosts the enrollment step and reads this router state.
      navigate('/portal/login', { replace: true, state: { mfa: { mfaToken: data.mfa_token, enrolled: data.enrolled } } });
      return;
    }
    toast({ title: 'Welcome aboard!', description: `You joined ${info?.tenant_name || 'the workspace'}.` });
    navigate('/portal', { replace: true });
  };

  const acceptNew = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/invitations/t/${encodeURIComponent(token)}/accept`, { name, password });
      if (data.access_token) completeGoogleLogin({ access_token: data.access_token, user: data.user });
      finish(data);
    } catch (e) {
      // The email got an account while this form was open (ticket 291): that
      // account must sign in to accept, then lands back on this link.
      if (codeOf(e) === 'login_required') { navigate('/portal/login', { state: { from: here } }); return; }
      toast({ title: 'Could not accept', description: messageOf(e) });
    }
    setBusy(false);
  };

  const acceptExisting = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invitations/t/${encodeURIComponent(token)}/accept`, {});
      await checkAuth(); // active tenant changed; reload the user through the auth shim
      finish(data);
    } catch (e) {
      if (codeOf(e) === 'already_member') { navigate('/portal', { replace: true }); return; }
      toast({ title: 'Could not accept', description: messageOf(e) });
    }
    setBusy(false);
  };

  const tagline = info ? `${info.tenant_name || 'A workspace'} wants you on the team as ${ROLE_LABELS[info.role] || info.role}.` : 'Join your team on BrokerForYou.';

  if (error) return <Shell title="Invitation unavailable" subtitle={error} tagline={tagline}><Link to="/portal/login" className="text-sm underline">Go to sign in</Link></Shell>;
  if (!info || authLoading) return <Shell title="Checking your invitation…" tagline={tagline}><Loader2 className="animate-spin text-slate-400" /></Shell>;

  // Branch 1: no account with that email yet — create one.
  if (!info.account_exists) {
    return (
      <Shell title={`Join ${info.tenant_name}`} subtitle={`Create your account for ${info.email_masked} to accept the ${ROLE_LABELS[info.role] || info.role} seat.`} tagline={tagline}>
        <form onSubmit={acceptNew} className="space-y-4">
          <div><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" data-testid="invite-name" /></div>
          <div><Label>Password</Label><PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required minLength={MIN_PASSWORD} containerClassName="mt-1.5" placeholder={PASSWORD_HINT} data-testid="invite-password" /></div>
          <Button type="submit" disabled={busy} variant="accent" className="w-full justify-center" data-testid="invite-accept-new">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Joining...</> : <>Create account & join <ArrowRight size={14} /></>}
          </Button>
        </form>
      </Shell>
    );
  }

  // Branch 2/3: an account exists — only that account, signed in, may accept.
  if (!user) {
    return (
      <Shell title={`Join ${info.tenant_name}`} subtitle={`This invitation was sent to ${info.email_masked}. Sign in to that account to accept it.`} tagline={tagline}>
        <Button variant="accent" className="w-full justify-center" data-testid="invite-go-login"
          onClick={() => navigate('/portal/login', { state: { from: here } })}>Sign in to accept <ArrowRight size={14} /></Button>
      </Shell>
    );
  }
  return (
    <Shell title={`Join ${info.tenant_name}`} subtitle={`Signed in as ${user.email}. Accept the ${ROLE_LABELS[info.role] || info.role} seat?`} tagline={tagline}>
      <div className="space-y-3">
        <Button variant="accent" className="w-full justify-center" disabled={busy} onClick={acceptExisting} data-testid="invite-accept-existing">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Joining...</> : <>Join workspace <ArrowRight size={14} /></>}
        </Button>
        <p className="text-xs text-slate-500">Not {info.email_masked}? Sign out and sign in with the invited account; invitations can only be accepted by the address they were sent to.</p>
      </div>
    </Shell>
  );
}
